import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  FormGroup,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import { Close, AccessTime as Clock, Save } from "@mui/icons-material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import biosenseAPI from "../../../../api/biosense";
import useFeedbackDialog from "../../../../utils/useFeedbackDialogue";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
);

const WaterValveControl = ({ device }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [waterValveStatus, setWaterValveStatus] = useState(false);
  const [waterValveOpenSeconds, setWaterValveOpenSeconds] = useState("");
  const [waterValveChartData, setWaterValveChartData] = useState([]);
  const [showWaterValveChart, setShowWaterValveChart] = useState(false);
  const [error, setError] = useState(null);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [waterValveCloseTime, setWaterValveCloseTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);

  // 水闸设置状态
  const [selectedHours, setSelectedHours] = useState(Array(24).fill(false));
  const [presetInterval, setPresetInterval] = useState("custom");
  const [openMinutes, setOpenMinutes] = useState("");
  const [openSeconds, setOpenSeconds] = useState("");
  const [selectedHoursCount, setSelectedHoursCount] = useState(0);

  //{t('latRange', { min: -90, max: 90 })}

  // 预设时间间隔选项
  const presetOptions = [
    { value: "every1", label: t("device.watervalve_everyhour") },
    { value: "every2", label: t("device.watervalve_everyxhours", { x: 2 }) },
    { value: "every3", label: t("device.watervalve_everyxhours", { x: 3 }) },
    { value: "every4", label: t("device.watervalve_everyxhours", { x: 4 }) },
    { value: "every6", label: t("device.watervalve_everyxhours", { x: 6 }) },
    { value: "every8", label: t("device.watervalve_everyxhours", { x: 8 }) },
    { value: "every12", label: t("device.watervalve_everyxhours", { x: 12 }) },
    { value: "custom", label: t("device.watervalve_customselection") },
  ];

  const { showSuccess, showError } = useFeedbackDialog();
  const localStorageKey = `${device.sn}_valve_on`;

  // 计算选中的小时数量
  useEffect(() => {
    setSelectedHoursCount(selectedHours.filter(Boolean).length);
  }, [selectedHours]);

  // 加载设备数据
  useEffect(() => {
    fetchDeviceData();
    const storedCloseTime = localStorage.getItem(localStorageKey);
    if (storedCloseTime) {
      const closeTime = parseInt(storedCloseTime);
      const now = Date.now();
      if (closeTime > now) {
        setWaterValveStatus(true);
        setWaterValveCloseTime(closeTime);
      } else {
        localStorage.removeItem(localStorageKey);
      }
    }
    fetchWaterValveHistory();
  }, []);

  // 倒计时效果
  useEffect(() => {
    let interval;

    if (waterValveStatus && waterValveCloseTime) {
      const calculateRemaining = () => {
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((waterValveCloseTime - now) / 1000));
        setTimeRemaining(remaining);

        if (remaining <= 2 && !buttonDisabled) {
          setButtonDisabled(true);
        }
      };

      calculateRemaining();
      interval = setInterval(calculateRemaining, 1000);
    }

    return () => clearInterval(interval);
  }, [waterValveStatus, waterValveCloseTime, buttonDisabled]);

  // 自动关闭水闸
  useEffect(() => {
    let timeout;

    if (waterValveStatus && waterValveCloseTime) {
      const now = Date.now();
      const delay = Math.max(0, waterValveCloseTime - now);

      timeout = setTimeout(() => {
        setWaterValveStatus(false);
        setWaterValveCloseTime(null);
        setButtonDisabled(false);
        localStorage.removeItem(localStorageKey);
      }, delay);
    }

    return () => clearTimeout(timeout);
  }, [waterValveStatus, waterValveCloseTime]);

  // 获取设备数据
  const fetchDeviceData = async () => {
    try {
      setLoading(true);

      // 获取水阀设置
      const waterValveSettings = await biosenseAPI.getWaterValveSettings({ sn: device.sn });
      const selectedHoursArray = Array(24).fill(false);
      waterValveSettings.selectedHours.forEach((hour) => {
        selectedHoursArray[hour] = true;
      });
      setSelectedHours(selectedHoursArray);

      // 计算预设间隔
      if (waterValveSettings.selectedHours.length === 24) {
        setPresetInterval("every1");
      } else if (
        waterValveSettings.selectedHours.length === 12 &&
        waterValveSettings.selectedHours.every((h, i) => h === i * 2)
      ) {
        setPresetInterval("every2");
      } else if (
        waterValveSettings.selectedHours.length === 8 &&
        waterValveSettings.selectedHours.every((h, i) => h === i * 3)
      ) {
        setPresetInterval("every3");
      } else if (
        waterValveSettings.selectedHours.length === 6 &&
        waterValveSettings.selectedHours.every((h, i) => h === i * 4)
      ) {
        setPresetInterval("every4");
      } else if (
        waterValveSettings.selectedHours.length === 4 &&
        waterValveSettings.selectedHours.every((h, i) => h === i * 6)
      ) {
        setPresetInterval("every6");
      } else if (
        waterValveSettings.selectedHours.length === 3 &&
        waterValveSettings.selectedHours.every((h, i) => h === i * 8)
      ) {
        setPresetInterval("every8");
      } else if (
        waterValveSettings.selectedHours.length === 2 &&
        waterValveSettings.selectedHours.every((h, i) => h === i * 12)
      ) {
        setPresetInterval("every12");
      } else {
        setPresetInterval("custom");
      }

      const totalSeconds = waterValveSettings.openSeconds;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      setOpenMinutes(minutes.toString());
      setOpenSeconds(seconds.toString());

      setTimeout(() => {
        setShowWaterValveChart(true);
      }, 800);
    } catch (error) {
      setError(error.message);
      showError("Load Failed", error.message);
    } finally {
      setLoading(false);
    }
  };

  // 处理秒数输入变化
  const handleWaterValveOpenSecondsChange = (e) => {
    const value = e.target.value;
    if (!isNaN(Number(value)) || value === "") {
      setWaterValveOpenSeconds(value);
    }
  };

  // 立即打开水闸
  const openWaterValveNow = async () => {
    if (!waterValveOpenSeconds) {
      showError("Validation Error", "Please enter the number of seconds to open the Water Valve");
      return;
    }

    try {
      setButtonDisabled(true);
      await biosenseAPI.openWaterValve({
        sn: device.sn,
        seconds: parseInt(waterValveOpenSeconds),
      });

      const closeTime = Date.now() + parseInt(waterValveOpenSeconds) * 1000;
      setWaterValveCloseTime(closeTime);
      localStorage.setItem(localStorageKey, closeTime.toString());
      setWaterValveStatus(true);
      showSuccess(
        "Action Successful",
        `Water Valve will open for ${waterValveOpenSeconds} seconds`,
      );
      setButtonDisabled(false);
    } catch (error) {
      setButtonDisabled(false);
      showError("Action Failed", error.message);
    }
  };

  // 立即关闭水闸
  const closeWaterValveNow = async () => {
    try {
      setButtonDisabled(true);
      await biosenseAPI.closeWaterValve({
        sn: device.sn,
      });
      setWaterValveStatus(false);
      setWaterValveCloseTime(null);
      localStorage.removeItem(localStorageKey);
      showSuccess("Action Successful", "Water Valve has been closed");
      setButtonDisabled(false);
    } catch (error) {
      setButtonDisabled(false);
      showError("Action Failed", error.message);
    }
  };

  // 保存水闸设置
  const handleSaveWaterValveSettings = async () => {
    const totalSeconds = parseInt(openMinutes || 0) * 60 + parseInt(openSeconds || 0);
    if (totalSeconds === 0) {
      showError("Validation Error", "Please enter valid minutes and seconds");
      return;
    }

    const selectedHoursIndices = selectedHours
      .map((isSelected, index) => (isSelected ? index : -1))
      .filter((index) => index !== -1);
    if (selectedHoursIndices.length === 0) {
      showError("Validation Error", "Please select at least one hour");
      return;
    }

    try {
      setButtonDisabled(true);
      await biosenseAPI.updateWaterValveSettings({
        sn: device.sn,
        selectedHours: selectedHoursIndices,
        openSeconds: totalSeconds,
      });

      showSuccess("Settings Saved", "Water Valve settings have been updated");
      setButtonDisabled(false);
    } catch (error) {
      showError("Save Failed", error.message);
      setButtonDisabled(false);
    }
  };

  // 处理小时选择
  const handleHourSelect = (index) => {
    const newSelectedHours = [...selectedHours];
    newSelectedHours[index] = !newSelectedHours[index];
    setSelectedHours(newSelectedHours);
    setPresetInterval("custom");
  };

  // 处理预设间隔选择
  const handlePresetSelect = (value) => {
    const newSelectedHours = Array(24).fill(false);

    if (value === "every1") {
      // 每小时
      for (let i = 0; i < 24; i++) {
        newSelectedHours[i] = true;
      }
    } else if (value === "every2") {
      // 每2小时
      for (let i = 0; i < 24; i += 2) {
        newSelectedHours[i] = true;
      }
    } else if (value === "every3") {
      // 每3小时
      for (let i = 0; i < 24; i += 3) {
        newSelectedHours[i] = true;
      }
    } else if (value === "every4") {
      // 每4小时
      for (let i = 0; i < 24; i += 4) {
        newSelectedHours[i] = true;
      }
    } else if (value === "every6") {
      // 每6小时
      for (let i = 0; i < 24; i += 6) {
        newSelectedHours[i] = true;
      }
    } else if (value === "every8") {
      // 每8小时
      for (let i = 0; i < 24; i += 8) {
        newSelectedHours[i] = true;
      }
    } else if (value === "every12") {
      // 每12小时
      for (let i = 0; i < 24; i += 12) {
        newSelectedHours[i] = true;
      }
    }

    setSelectedHours(newSelectedHours);
    setPresetInterval(value);
  };

  // 计算总秒数
  const totalSeconds = parseInt(openMinutes || 0) * 60 + parseInt(openSeconds || 0);

  const convertToUTC8 = (utcTime) => {
    // 创建一个 UTC 时间的 Date 对象
    const date = new Date(utcTime);

    // 添加 8 小时
    date.setUTCHours(date.getUTCHours() + 8);

    // 格式化输出，保留日期和时间
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // 简化数据点
  const simplifyData = (data) => {
    if (data.length <= 1) return data;

    const simplified = [];
    let prevPoint = data[0];

    // 添加第一个点
    simplified.push({ ...prevPoint });

    for (let i = 1; i < data.length; i++) {
      const currentPoint = data[i];

      // 如果状态发生变化
      if (currentPoint.open !== prevPoint.open) {
        // 添加前一个状态的结束点（与当前点时间相同，但状态为前一个状态）
        simplified.push({
          time: currentPoint.time,
          open: prevPoint.open,
        });

        // 添加新状态的开始点
        simplified.push({ ...currentPoint });
      }

      prevPoint = currentPoint;
    }

    // 添加最后一个点
    simplified.push({ ...prevPoint });

    return simplified;
  };
  const convertedData = waterValveChartData.map((item) => ({
    time: convertToUTC8(item.time),
    open: item.open,
  }));

  //const simplifiedData = convertedData
  const simplifiedData = simplifyData(convertedData);

  // 获取水闸历史数据
  const fetchWaterValveHistory = async () => {
    try {
      const historyData = await biosenseAPI.getWaterValveHistory({ sn: device.sn });
      const now = new Date();
      const twoHoursAgo = new Date(now.getTime() - 2 * 60 * 60 * 1000);

      const processedData = [];
      const step = 1;
      for (let d = new Date(twoHoursAgo); d <= now; d.setSeconds(d.getSeconds() + step)) {
        const timestamp = d.getTime() / 1000;
        let open = 0;
        historyData.forEach((item) => {
          const start = new Date(item.datetime).getTime() / 1000;
          const end = start + item.duration;
          if (timestamp >= start && timestamp < end) {
            open = 1;
            console.log(start, timestamp, end);
          }
        });
        processedData.push({
          time: timestamp * 1000,
          open,
        });
      }
      setWaterValveChartData(processedData);
    } catch (error) {
      setError(error.message);
      showError("Load History Failed", error.message);
    }
  };

  // 图表配置
  const chartData = {
    labels: simplifiedData.map((item) => item.time),
    datasets: [
      {
        label: "Water Valve Status",
        data: simplifiedData.map((item) => ({
          x: new Date(item.time), // 使用Date对象作为x值
          y: item.open,
        })),
        borderColor: "#3f51b5",
        backgroundColor: "rgba(63, 81, 181, 0.1)",
        tension: 0,
        fill: true,
        pointBackgroundColor: "#3f51b5",
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    height: 300,
    scales: {
      y: {
        min: 0,
        max: 1,
        ticks: {
          stepSize: 1,
          callback: function (value) {
            return value === 1 ? t("device.watervalve_open") : t("device.watervalve_closed");
          },
        },
      },
      x: {
        type: "time", // 设置为时间轴
        time: {
          unit: "hour", // 时间单位为小时
          displayFormats: {
            hour: "MM/DD HH:mm", // 时间显示格式
          },
          tooltipFormat: "MM/DD HH:mm", // 鼠标悬停时的时间格式
        },
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 0, // 标签不旋转
          autoSkip: true, // 自动跳过密集的标签
          maxTicksLimit: 8, // 最多显示8个标签
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return context.raw.y === 1 ? t("device.watervalve_open") : t("device.watervalve_closed");
          },
          title: function (context) {
            // 格式化时间显示
            return new Date(context[0].raw.x).toLocaleString();
          },
        },
      },
    },
  };

  // 处理分钟输入变化
  const handleOpenMinutesChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 59) {
      setOpenMinutes(value.toString());
    }
  };

  // 处理秒数输入变化
  const handleOpenSecondsChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 59) {
      setOpenSeconds(value.toString());
    }
  };

  if (loading) {
    return (
      <Card
        sx={{ boxShadow: 0, borderRadius: 12, border: "1px solid #e0e0e0", overflow: "hidden" }}
      >
        <CardContent sx={{ p: 6 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <Clock sx={{ mr: 2, color: "#3f51b5" }} />
            <Typography variant='h5' sx={{ color: "#333", fontWeight: 600 }}>
              {t("device.watervalve_watervalveschedule")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ boxShadow: 0, borderRadius: 12, border: "1px solid #e0e0e0", overflow: "hidden" }}>
      <CardContent sx={{ p: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <Clock sx={{ mr: 2, color: "#3f51b5" }} />
          <Typography variant='h5' sx={{ color: "#333", fontWeight: 600 }}>
            {t("device.watervalve_watervalveschedule")}
          </Typography>
          {/* <Tooltip title="How to use this feature">
            <IconButton size="small" color="primary">
              <HelpOutline fontSize="small" />
            </IconButton>
          </Tooltip> */}
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 6 }}>
          <FormGroup>
            <FormControlLabel
              control={<Switch checked={waterValveStatus} color='primary' />}
              label={
                waterValveStatus
                  ? `${t("device.watervalve_watervalve")}: ${t("device.watervalve_open")}`
                  : `${t("device.watervalve_watervalve")}: ${t("device.watervalve_closed")}`
              }
            />
          </FormGroup>
        </Box>

        {/* 立即操作部分 */}
        <Box sx={{ mb: 6, p: 4, backgroundColor: "#f5f5f5", borderRadius: 8 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 3 }}>
            {t("device.watervalve_immediateoperation")}
          </Typography>
          <Grid container spacing={3} alignItems='center'>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("device.watervalve_openfor(seconds)")}
                value={waterValveOpenSeconds}
                onChange={handleWaterValveOpenSecondsChange}
                type='number'
                placeholder={t("device.watervalve_seconds")}
                disabled={waterValveStatus}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {waterValveStatus ? (
                <Button
                  fullWidth
                  variant='contained'
                  color='error'
                  onClick={closeWaterValveNow}
                  disabled={buttonDisabled}
                  sx={{ height: 56 }}
                >
                  <Close fontSize='small' sx={{ mr: 1 }} />
                  {t("device.watervalve_closenow")}
                  {timeRemaining > 0 && (
                    <span sx={{ ml: 1, fontWeight: "bold" }}>
                      ({timeRemaining}
                      {t("device.watervalve_seconds(unit)")})
                    </span>
                  )}
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant='contained'
                  color='primary'
                  onClick={openWaterValveNow}
                  disabled={!waterValveOpenSeconds || buttonDisabled}
                  sx={{ height: 56 }}
                >
                  {t("device.watervalve_opennow")}
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* 预设时间选择器 */}
        <Box sx={{ mb: 6 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 3 }}>
            {t("device.watervalve_scheduledhours")}
          </Typography>

          <FormControl fullWidth>
            <InputLabel id='preset-interval-label'>Select Schedule</InputLabel>
            <Select
              labelId='preset-interval-label'
              id='preset-interval'
              value={presetInterval}
              label='Select Schedule'
              onChange={(e) => handlePresetSelect(e.target.value)}
            >
              {presetOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* 小时选择网格 */}
          <Box sx={{ mt: 4 }}>
            <Typography variant='body2' color='text.secondary' mb={2}>
              {selectedHoursCount > 0
                ? t("device.watervalve_xhouryselected", {
                    x: selectedHoursCount,
                    y: selectedHoursCount > 1 ? "s" : "",
                  })
                : t("device.watervalve_nohoursselected")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                border: "1px solid #e0e0e0",
                borderRadius: 4,
                p: 1,
                backgroundColor: "#fafafa",
              }}
            >
              {Array.from({ length: 24 }, (_, index) => (
                <button
                  key={index}
                  type='button'
                  onClick={() => handleHourSelect(index)}
                  style={{
                    width: "calc(8.333% - 1px)",
                    height: 40,
                    margin: 0,
                    padding: 0,
                    border: "none",
                    borderRadius: 2,
                    backgroundColor: selectedHours[index] ? "#3f51b5" : "#e0e0e0",
                    color: selectedHours[index] ? "white" : "#333",
                    fontSize: 12,
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                    outline: "none",
                    "&:hover": {
                      opacity: 0.8,
                    },
                  }}
                >
                  {index + 1}
                </button>
              ))}
            </Box>
          </Box>
        </Box>

        {/* 持续时间设置 */}
        <Box sx={{ mb: 6 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 3 }}>
            {t("device.watervalve_openduration")}
          </Typography>

          <Grid container spacing={3} alignItems='center'>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t("device.watervalve_minutes")}
                value={openMinutes}
                onChange={handleOpenMinutesChange}
                type='number'
                placeholder='0-59'
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t("device.watervalve_seconds")}
                value={openSeconds}
                onChange={handleOpenSecondsChange}
                type='number'
                placeholder='0-59'
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                variant='body1'
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 56,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 4,
                  fontWeight: 600,
                }}
              >
                {t("device.watervalve_total")}: {totalSeconds} {t("device.watervalve_seconds")}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* 保存按钮 */}
        <Button
          fullWidth
          variant='contained'
          color='primary'
          onClick={handleSaveWaterValveSettings}
          disabled={buttonDisabled}
          sx={{ height: 56 }}
        >
          <Save fontSize='small' sx={{ mr: 1 }} />
          {t("device.watervalve_savewatervalvesettings")}
        </Button>

        {/* 活动图表 */}
        <Box sx={{ mt: 8 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 3 }}>
            {t("device.watervalve_watervalveactivity(last2hours)")}
          </Typography>

          {showWaterValveChart ? (
            <Box sx={{ height: 200 }}>
              <Line data={chartData} options={chartOptions} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default WaterValveControl;
